import * as React from 'react'

import { Box, Icon, Skeleton, Tabs, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import CycleSelector from 'modules/surveys/components/Analytics/CycleSelector'
import ScoreResults from 'modules/surveys/components/Analytics/ScoreResults'
import TableResults from 'modules/surveys/components/Analytics/TableResults'
import Filters from 'modules/surveys/components/Filters'
import filterFields from 'modules/surveys/components/Filters/filterFields'
import useAnalyticsMultiFilters, {
  AnalyticsFilterForm,
} from 'modules/surveys/hooks/useAnalyticsMultiFilters'
import { useAnalytics } from 'modules/surveys/pages/Show/hooks'

import AnalyticsActions from './RightActions'

const TABS = {
  Summary: 'summary',
  Table: 'table',
}

export const Analytics = observer(() => {
  const {
    form: surveyModel,
    canEditSurvey,
    loading,
    cycle,
    setCycle,
    analyticResults,
    surveyAnalyticsFilter,
    setSurveyAnalyticsFilter,
    questionGroups,
  } = useAnalytics()

  const {
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    localFilter,
  } = useAnalyticsMultiFilters(setSurveyAnalyticsFilter)

  const [tabs, setTabs] = React.useState([
    {
      id: TABS.Summary,
      active: true,
      label: i18n.t('surveys.analytics.tabs.summary').toUpperCase(),
    },
    {
      id: TABS.Table,
      active: false,
      label: i18n.t('surveys.analytics.tabs.table').toUpperCase(),
    },
  ])

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading: loadingFilters } = useAsyncData(async () => {
    const fields = await filterFields()

    setFields(fields)
  }, [])

  if (loading) {
    return <Loading />
  }

  const description = surveyModel?.hasSchedule
    ? i18n.t('surveys.analytics.description_schedule')
    : i18n.t('surveys.analytics.description')

  const isFilterApplied = !!surveyAnalyticsFilter?.default.length

  return (
    <PanesPad>
      <Box gap="s16" paddingBottom="s32" alignItems="flexStart">
        <Box>
          <Icon.Large icon="surveys" />
        </Box>
        <Text.Header2>{i18n.t('surveys.analytics.title')}</Text.Header2>
        <Text.Normal>{description}</Text.Normal>
        <Box flexDirection="row" gap="s16" alignItems="center">
          {loadingFilters && <Skeleton width="s96" height="s32" />}
          {!loadingFilters && (
            <Filters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(isActive, defaultFilter) => {
                onApplyFilters(isActive, defaultFilter)
                setSurveyAnalyticsFilter(
                  new AnalyticsFilterForm(filters, logicOperator.values, defaultFilter, isActive)
                )
              }}
              onCancel={onCancel}
              filterKey={localFilter}
            />
          )}
          {surveyModel?.hasSchedule && !loading && (
            <CycleSelector analyticResults={analyticResults} cycle={cycle} setCycle={setCycle} />
          )}
          <AnalyticsActions
            surveyAnalyticsFilter={surveyAnalyticsFilter}
            form={surveyModel}
            canEditSurvey={canEditSurvey}
            hasAnswers={!!questionGroups.answered.length}
          />
        </Box>
      </Box>
      {loading && <Loading />}
      {!loading && (
        <Box gap="s16">
          <Tabs
            currentId={tabs.find((tab) => tab.active)?.id ?? ''}
            onTabClicked={(tabId) => {
              setTabs(
                tabs.map((tab) => {
                  if (tab.id === tabId) {
                    return {
                      ...tab,
                      active: true,
                    }
                  }
                  return {
                    ...tab,
                    active: false,
                  }
                })
              )
            }}
          >
            {tabs.map(({ id, label }) => (
              <Tabs.Tab key={id} id={id} label={label}>
                <Box paddingTop="s40">
                  {id === TABS.Summary && <ScoreResults isFilterApplied={isFilterApplied} />}
                  {id === TABS.Table && <TableResults isFilterApplied={isFilterApplied} />}
                </Box>
              </Tabs.Tab>
            ))}
          </Tabs>
        </Box>
      )}
    </PanesPad>
  )
})
